import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { useRestClient } from '@/contexts/QueryProvider'
import { cn } from '@/lib/utils'
import { IconSearchOutline } from '@icons/IconSearchOutline'
import { IconV2DownloadOutline } from '@icons/IconV2DownloadOutline'
import { IconV2FileOutline } from '@icons/IconV2FileOutline'
import { IconV2FolderOutline } from '@icons/IconV2FolderOutline'
import { AlertCircle, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import {
  buildDmpChatSessionFileTree,
  createDmpChatSessionFileDownloadParameters,
  filterDmpChatSessionFileTree,
  formatDmpChatFileSize,
  readDmpChatSessionFileBlob,
  type DmpChatSessionFileFolderNode,
  type DmpChatSessionFileNode,
} from '../dmpChatSessionFiles'
import { useDmpChatRuntime } from './DmpChatRuntimeProvider'

interface DmpChatSessionFilesProps {
  organisationId: number | undefined
  projectId: number
  chatHistoryId: string
  isActive: boolean
}

export function DmpChatSessionFiles({ organisationId, projectId, chatHistoryId, isActive }: DmpChatSessionFilesProps) {
  const { getChatHistoryApiDownloadSessionFile } = useRestClient()
  const {
    mockModeEnabled,
    refetchSessionFiles,
    sessionFiles,
    sessionFilesError,
    sessionFilesFetching,
    sessionFilesLoading,
  } = useDmpChatRuntime()
  const [searchValue, setSearchValue] = useState('')
  const [expandedFolderIds, setExpandedFolderIds] = useState<ReadonlySet<string>>(
    () => new Set(['folder:uploaded-files', 'folder:artifacts'])
  )
  const [downloadingFileIds, setDownloadingFileIds] = useState<ReadonlySet<string>>(() => new Set())
  const [downloadErrors, setDownloadErrors] = useState<ReadonlyMap<string, string>>(() => new Map())

  const fileTree = useMemo(() => buildDmpChatSessionFileTree(sessionFiles), [sessionFiles])
  const visibleFileTree = useMemo(() => filterDmpChatSessionFileTree(fileTree, searchValue), [fileTree, searchValue])

  const toggleFolder = useCallback((folderId: string) => {
    setExpandedFolderIds((currentFolderIds) => {
      const nextFolderIds = new Set(currentFolderIds)
      if (nextFolderIds.has(folderId)) {
        nextFolderIds.delete(folderId)
      } else {
        nextFolderIds.add(folderId)
      }
      return nextFolderIds
    })
  }, [])

  const handleDownload = useCallback(
    async (file: DmpChatSessionFileNode) => {
      if (mockModeEnabled || organisationId == null || !chatHistoryId.trim()) {
        return
      }

      setDownloadingFileIds((currentFileIds) => new Set(currentFileIds).add(file.id))
      setDownloadErrors((currentErrors) => {
        const nextErrors = new Map(currentErrors)
        nextErrors.delete(file.id)
        return nextErrors
      })

      try {
        const result = await getChatHistoryApiDownloadSessionFile({
          parameters: createDmpChatSessionFileDownloadParameters({ organisationId, projectId, chatHistoryId }, file),
        } as Parameters<typeof getChatHistoryApiDownloadSessionFile>[0])

        if (!result.response?.ok) {
          throw new Error(getDownloadFailureMessage(result.response?.status))
        }

        const blob = await readDmpChatSessionFileBlob(result)
        triggerBrowserDownload(blob, file.fileName)
      } catch (error) {
        setDownloadErrors((currentErrors) => {
          const nextErrors = new Map(currentErrors)
          nextErrors.set(file.id, getDownloadErrorMessage(error, file.name))
          return nextErrors
        })
      } finally {
        setDownloadingFileIds((currentFileIds) => {
          const nextFileIds = new Set(currentFileIds)
          nextFileIds.delete(file.id)
          return nextFileIds
        })
      }
    },
    [chatHistoryId, getChatHistoryApiDownloadSessionFile, mockModeEnabled, organisationId, projectId]
  )

  if (!isActive) {
    return null
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="relative shrink-0">
        <IconSearchOutline
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-2.5 size-4 opacity-50"
        />
        <Input
          aria-label="Filter DMP Chat files"
          className="h-9 pl-9"
          onChange={(event) => setSearchValue(event.target.value)}
          placeholder="Search files..."
          type="search"
          value={searchValue}
        />
      </div>

      {mockModeEnabled && (
        <p className="px-1 text-xs text-muted-foreground" role="status">
          Mock response mode — no stored files are loaded.
        </p>
      )}

      {!mockModeEnabled && organisationId == null && (
        <p className="flex items-center gap-2 px-1 text-xs text-muted-foreground" role="alert">
          <AlertCircle className="size-3.5" />
          Files are unavailable without an organisation.
        </p>
      )}

      {!mockModeEnabled && organisationId != null && sessionFilesLoading && (
        <div className="flex items-center gap-2 px-1 py-2 text-sm text-muted-foreground" role="status">
          <Loader2 className="size-4 animate-spin" />
          Loading files…
        </div>
      )}

      {!mockModeEnabled && organisationId != null && sessionFilesError && (
        <div className="flex flex-col items-start gap-2 px-1 py-2 text-sm text-muted-foreground" role="alert">
          <span className="flex items-center gap-2">
            <AlertCircle className="size-4" />
            Files are unavailable. Try again.
          </span>
          <Button onClick={refetchSessionFiles} size="xs" type="button" variant="outline">
            Try again
          </Button>
        </div>
      )}

      {!mockModeEnabled && organisationId != null && sessionFilesFetching && !sessionFilesLoading && (
        <div className="flex items-center gap-2 px-1 text-xs text-muted-foreground" role="status">
          <Loader2 className="size-3.5 animate-spin" />
          Refreshing files…
        </div>
      )}

      {(mockModeEnabled || !sessionFilesError) && (
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div className="flex flex-col gap-1">
            <SessionFileFolder
              folder={visibleFileTree.uploadedFiles}
              expandedFolderIds={expandedFolderIds}
              downloadingFileIds={downloadingFileIds}
              downloadErrors={downloadErrors}
              isSearching={searchValue.trim().length > 0}
              onDownload={handleDownload}
              onToggle={toggleFolder}
            />
            <SessionFileFolder
              folder={visibleFileTree.artifacts}
              expandedFolderIds={expandedFolderIds}
              downloadingFileIds={downloadingFileIds}
              downloadErrors={downloadErrors}
              isSearching={searchValue.trim().length > 0}
              onDownload={handleDownload}
              onToggle={toggleFolder}
            />
          </div>
        </div>
      )}
    </div>
  )
}

function SessionFileFolder({
  folder,
  expandedFolderIds,
  downloadingFileIds,
  downloadErrors,
  isSearching,
  onDownload,
  onToggle,
  depth = 0,
}: {
  folder: DmpChatSessionFileFolderNode
  expandedFolderIds: ReadonlySet<string>
  downloadingFileIds: ReadonlySet<string>
  downloadErrors: ReadonlyMap<string, string>
  isSearching: boolean
  onDownload: (file: DmpChatSessionFileNode) => void
  onToggle: (folderId: string) => void
  depth?: number
}) {
  const isExpanded = isSearching || expandedFolderIds.has(folder.id)
  const contentsId = `${folder.id}-contents`

  return (
    <div className="min-w-0">
      <button
        aria-controls={contentsId}
        aria-expanded={isExpanded}
        aria-label={`${folder.name} folder`}
        className={cn(
          'flex w-full min-w-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          depth > 0 && 'text-muted-foreground'
        )}
        onClick={() => onToggle(folder.id)}
        type="button"
      >
        {isExpanded ? (
          <ChevronDown aria-hidden="true" className="size-4 shrink-0" />
        ) : (
          <ChevronRight aria-hidden="true" className="size-4 shrink-0" />
        )}
        <IconV2FolderOutline aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 truncate">{folder.name}</span>
      </button>
      {isExpanded && (
        <div className="ml-2 border-l pl-3" id={contentsId}>
          {folder.children.length === 0 ? (
            <p className="px-2 py-1 text-xs text-muted-foreground">
              {isSearching ? 'No matching files.' : 'No files in this folder.'}
            </p>
          ) : (
            folder.children.map((node) =>
              node.nodeKind === 'folder' ? (
                <SessionFileFolder
                  key={node.id}
                  folder={node}
                  expandedFolderIds={expandedFolderIds}
                  downloadingFileIds={downloadingFileIds}
                  downloadErrors={downloadErrors}
                  isSearching={isSearching}
                  onDownload={onDownload}
                  onToggle={onToggle}
                  depth={depth + 1}
                />
              ) : (
                <SessionFileRow
                  key={node.id}
                  file={node}
                  isDownloading={downloadingFileIds.has(node.id)}
                  downloadError={downloadErrors.get(node.id)}
                  onDownload={onDownload}
                />
              )
            )
          )}
        </div>
      )}
    </div>
  )
}

function SessionFileRow({
  file,
  isDownloading,
  downloadError,
  onDownload,
}: {
  file: DmpChatSessionFileNode
  isDownloading: boolean
  downloadError: string | undefined
  onDownload: (file: DmpChatSessionFileNode) => void
}) {
  const formattedSize = formatDmpChatFileSize(file.sizeBytes)

  return (
    <div aria-label={`${file.name} file`} className="min-w-0 rounded-md px-2 py-1 hover:bg-muted" role="group">
      <div className="flex min-w-0 items-center gap-1.5">
        <IconV2FileOutline aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-sm" title={file.name}>
          {file.name}
        </span>
        {formattedSize && <span className="shrink-0 text-[11px] text-muted-foreground">{formattedSize}</span>}
        <Button
          aria-label={`Download ${file.name}`}
          className="size-7 shrink-0"
          disabled={isDownloading}
          onClick={() => onDownload(file)}
          title={`Download ${file.name}`}
          type="button"
          variant="ghost"
        >
          {isDownloading ? (
            <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
          ) : (
            <IconV2DownloadOutline aria-hidden="true" className="size-3.5" />
          )}
        </Button>
      </div>
      {downloadError && (
        <p className="pl-5 text-xs text-destructive" role="alert">
          {downloadError}
        </p>
      )}
    </div>
  )
}

function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
}

function getDownloadFailureMessage(status: number | undefined): string {
  if (status === 400) {
    return 'The file request was invalid.'
  }
  if (status === 404) {
    return 'The file is no longer available.'
  }
  return status ? `The file request failed (${status}).` : 'The file response was invalid.'
}

function getDownloadErrorMessage(error: unknown, fileName: string): string {
  if (error instanceof Error && error.message) {
    return `Could not download ${fileName}: ${error.message} Try again.`
  }
  return `Could not download ${fileName}. Check your connection and try again.`
}
